import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Info, Check, AlertTriangle, Map, Grid, List, BarChart, Vote, Users, Shuffle } from 'lucide-react';

const VotingSystems = () => {
  // State to track which system is being compared
  const [activeSystem, setActiveSystem] = useState('plurality');
  // State to track window width for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update window width when resized
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if we're on mobile/tablet
  const isMobile = windowWidth < 768;
  
  // Color palette from your existing app
  const colorPalette = {
    primary: '#922c40',   // Burgundy/wine red
    secondary: '#DC9750', // Bronze/gold
    tertiary: '#F3EAC0',  // Cream/beige
    light: '#F9F5E3',     // Lighter cream for backgrounds
    dark: '#1E2640',      // Navy/dark blue
    white: '#FFFFFF',
    black: '#333333',
    accent: '#922c40'
  };

  // Helper function to generate system cards
  const SystemCard = ({ title, icon, description, pros, cons, fairnessScore, representationScore, simplicity, activeInCountries, onClick, isActive }) => {
    const Icon = icon;
    
    return (
      <div 
        className={`system-card ${isActive ? 'active' : ''}`}
        style={{
          backgroundColor: isActive ? colorPalette.light : 'white',
          borderColor: isActive ? colorPalette.primary : '#e0e0e0',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          border: `1px solid ${isActive ? colorPalette.primary : '#e0e0e0'}`,
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '16px',
          boxShadow: isActive ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
        }}
        onClick={onClick}
      >
        <div className="system-header" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '12px',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              backgroundColor: isActive ? colorPalette.primary : colorPalette.secondary,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <Icon size={20} color="white" />
            </div>
            <h3 style={{ 
              margin: 0,
              color: isActive ? colorPalette.primary : colorPalette.dark,
              fontWeight: '600',
              fontSize: isMobile ? '1.1rem' : '1.25rem'
            }}>{title}</h3>
          </div>
          
          {isActive && (
            <div style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              padding: '4px 8px',
              fontSize: '0.7rem',
              borderRadius: '4px',
              fontWeight: '600'
            }}>SELECTED</div>
          )}
        </div>
        
        <p style={{ 
          fontSize: '0.9rem', 
          color: isActive ? colorPalette.dark : '#666',
          marginBottom: '12px' 
        }}>{description}</p>
        
        {isActive && (
          <div className="system-details" style={{ marginTop: '16px' }}>
            <div className="scores" style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              marginBottom: '16px',
              borderBottom: `1px solid ${colorPalette.tertiary}`,
              paddingBottom: '16px',
              gap: isMobile ? '12px' : '0'
            }}>
              <div className="score-item">
                <span style={{ 
                  fontSize: '0.8rem', 
                  color: '#666', 
                  display: 'block',
                  marginBottom: '4px' 
                }}>Fairness</span>
                <div style={{ display: 'flex' }}>
                  {[1, 2, 3, 4, 5].map(score => (
                    <div key={score} style={{
                      width: '18px',
                      height: '8px',
                      backgroundColor: score <= fairnessScore ? colorPalette.primary : '#e0e0e0',
                      marginRight: '3px',
                      borderRadius: '4px'
                    }} />
                  ))}
                </div>
              </div>
              
              <div className="score-item">
                <span style={{ 
                  fontSize: '0.8rem', 
                  color: '#666', 
                  display: 'block',
                  marginBottom: '4px' 
                }}>Representation</span>
                <div style={{ display: 'flex' }}>
                  {[1, 2, 3, 4, 5].map(score => (
                    <div key={score} style={{
                      width: '18px',
                      height: '8px',
                      backgroundColor: score <= representationScore ? colorPalette.primary : '#e0e0e0',
                      marginRight: '3px',
                      borderRadius: '4px'
                    }} />
                  ))}
                </div>
              </div>
              
              <div className="score-item">
                <span style={{ 
                  fontSize: '0.8rem', 
                  color: '#666', 
                  display: 'block',
                  marginBottom: '4px' 
                }}>Simplicity</span>
                <div style={{ display: 'flex' }}>
                  {[1, 2, 3, 4, 5].map(score => (
                    <div key={score} style={{
                      width: '18px',
                      height: '8px',
                      backgroundColor: score <= simplicity ? colorPalette.primary : '#e0e0e0',
                      marginRight: '3px',
                      borderRadius: '4px'
                    }} />
                  ))}
                </div>
              </div>
            </div>
            
            <div className="pros-cons" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '20px',
              marginBottom: '16px'
            }}>
              <div className="pros">
                <h4 style={{ 
                  fontSize: '0.9rem',
                  marginBottom: '8px',
                  color: '#2e7d32',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Check size={16} style={{ marginRight: '4px' }} />
                  Pros
                </h4>
                <ul style={{
                  listStyleType: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: '0.85rem'
                }}>
                  {pros.map((pro, index) => (
                    <li key={index} style={{
                      marginBottom: '6px',
                      paddingLeft: '16px',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        top: '8px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: '#2e7d32'
                      }}></span>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="cons">
                <h4 style={{ 
                  fontSize: '0.9rem',
                  marginBottom: '8px',
                  color: colorPalette.primary,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <AlertTriangle size={16} style={{ marginRight: '4px' }} />
                  Cons
                </h4>
                <ul style={{
                  listStyleType: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: '0.85rem'
                }}>
                  {cons.map((con, index) => (
                    <li key={index} style={{
                      marginBottom: '6px',
                      paddingLeft: '16px',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        top: '8px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: colorPalette.primary
                      }}></span>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="active-countries" style={{
              backgroundColor: colorPalette.tertiary,
              padding: '12px',
              borderRadius: '6px',
              fontSize: '0.85rem'
            }}>
              <strong>Active in:</strong> {activeInCountries}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Mobile-friendly system comparison card for the redesigned table
  const ComparisonCard = ({ system, type, proportionality, voterChoice, complexity }) => {
    // Helper function to get color based on value
    const getValueColor = (value) => {
      if (value.includes('Very High') || value.includes('High')) return '#2e7d32';
      if (value.includes('Medium')) return '#ef6c00';
      if (value.includes('Low')) return '#d32f2f';
      return '#333';
    };
    
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '1.1rem',
          color: colorPalette.dark,
          borderBottom: `2px solid ${colorPalette.primary}`,
          paddingBottom: '8px'
        }}>{system}</h3>
        
        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>Type:</span>
          <span style={{ fontSize: '0.85rem' }}>{type}</span>
        </div>
        
        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>Proportionality:</span>
          <span style={{ fontSize: '0.85rem', color: getValueColor(proportionality) }}>{proportionality}</span>
        </div>
        
        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>Voter Choice:</span>
          <span style={{ fontSize: '0.85rem', color: getValueColor(voterChoice) }}>{voterChoice}</span>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>Complexity:</span>
          <span style={{ fontSize: '0.85rem', color: getValueColor(complexity) }}>{complexity}</span>
        </div>
      </div>
    );
  };

  // Data for single-member district systems
  const smdSystems = [
    {
      id: 'plurality',
      title: 'Plurality/Majority (FPTP)',
      icon: Vote,
      description: 'The candidate with the most votes wins, even without a majority. Also known as "First Past The Post" or "Winner-Takes-All".',
      pros: [
        'Simple to understand and implement',
        'Clear connection between voters and their representative',
        'Usually produces stable, single-party governments',
        'Straightforward ballot counting process'
      ],
      cons: [
        'Can result in "wasted votes" when candidates win with less than 50%',
        'Creates pressure to vote strategically rather than sincerely',
        'Typically leads to two-party domination and underrepresentation of minority views',
        'Vulnerable to gerrymandering',
        'Often creates "safe seats" with little competition'
      ],
      fairnessScore: 2,
      representationScore: 1,
      simplicity: 5,
      activeInCountries: 'United States, United Kingdom, Canada, India'
    },
    {
      id: 'at-large',
      title: 'At-Large Plurality',
      icon: Users,
      description: 'Voters cast votes for multiple candidates in a jurisdiction-wide election. Candidates with the most votes win the available seats.',
      pros: [
        'Simple extension of plurality voting',
        'Eliminates district-based gerrymandering',
        'Can increase representation of geographically dispersed groups',
        'Allows voters to support multiple candidates'
      ],
      cons: [
        'Can lead to majority party winning all seats ("winner-take-all" effect)',
        'May still underrepresent minority groups',
        'Long ballots can be confusing when many candidates run',
        'Weakens geographic connection between voters and representatives',
        'May reduce representation of rural areas'
      ],
      fairnessScore: 2,
      representationScore: 2,
      simplicity: 4,
      activeInCountries: 'Used in some US local elections, historic use in some congressional elections'
    },
    {
      id: 'ranked-choice',
      title: 'Ranked Choice Voting (RCV/IRV)',
      icon: Shuffle,
      description: 'Voters rank candidates in order of preference. If no candidate wins a majority, the lowest-performing candidate is eliminated and their votes redistributed until someone reaches 50%+.',
      pros: [
        'Eliminates the "spoiler effect" and reduces strategic voting',
        'Winners have broader support from voters',
        'Encourages positive campaigning',
        'Can improve representation for minority viewpoints',
        'Eliminates need for costly runoff elections'
      ],
      cons: [
        "More complex ballot counting than plurality",
        "Still maintains single-member districts with potential for gerrymandering",
        "Doesn't guarantee proportional representation across a legislature",
        "Requires either hand-counting or specialized voting equipment"
      ],
      fairnessScore: 4,
      representationScore: 3,
      simplicity: 3,
      activeInCountries: 'Australia, Ireland, New Zealand (local elections), Some US cities including San Francisco, Minneapolis, and Maine/Alaska for statewide elections'
    }
  ];

  // Data for multi-member district systems
  const mmdSystems = [
    {
      id: 'open-list-pr',
      title: 'Open-List Proportional Representation (OLPR)',
      icon: List,
      description: 'Voters select both a party and can indicate preferences for specific candidates within that party. Seats are allocated proportionally to parties, then to candidates based on preference votes.',
      pros: [
        'Achieves proportional representation at the party level',
        'Gives voters influence over which candidates get elected within parties',
        'Maintains connection between voters and representatives',
        'Allows new parties to gain representation with modest electoral support',
        'Resistant to gerrymandering effects'
      ],
      cons: [
        'More complex than closed-list systems',
        'May lead to intra-party competition and fragmentation',
        'Might favor candidates with name recognition or resources',
        'Can lead to coalition governments, which some view as less stable',
        'May result in small parties holding disproportionate power in coalitions'
      ],
      fairnessScore: 5,
      representationScore: 5,
      simplicity: 3,
      activeInCountries: 'Finland, Sweden, Denmark, Brazil, Chile, Italy'
    },
    {
      id: 'closed-list-pr',
      title: 'Closed-List Proportional Representation (CLPR)',
      icon: BarChart,
      description: 'Voters select a party, not individual candidates. Parties receive seats proportional to their vote share, and fill those seats with candidates in order of a pre-determined list.',
      pros: [
        'High degree of proportionality between votes and seats',
        'Simple for voters - just choose one party',
        'Parties can ensure balanced representation (gender, ethnicity, etc.)',
        'Enables representation of smaller parties and minority viewpoints',
        'Resistant to gerrymandering'
      ],
      cons: [
        'Weakens direct connection between voters and representatives',
        'Gives party leadership significant control over who gets elected',
        'May prioritize party loyalty over constituent responsiveness',
        'Can lead to fragmented multi-party legislatures',
        'May lead to difficult coalition building'
      ],
      fairnessScore: 4,
      representationScore: 4,
      simplicity: 5,
      activeInCountries: 'Spain, South Africa, Israel, Portugal, Turkey'
    },
    {
      id: 'mmpr',
      title: 'Mixed-Member PR (MMPR)',
      icon: Grid,
      description: 'A hybrid system: some representatives are elected in single-member districts, while others are elected from party lists to ensure overall proportionality.',
      pros: [
        'Combines geographic representation with proportional outcomes',
        'Voters maintain a direct representative while ensuring proportionality',
        'Reduces wasted votes through compensatory list seats',
        'Balances the benefits of different electoral systems',
        'Allows smaller parties to gain representation'
      ],
      cons: [
        'More complex to understand and implement than pure systems',
        'Creates two "classes" of representatives with different mandates',
        'May still incentivize strategic voting in the district portion',
        'Can be confusing for voters with separate votes for districts and parties',
        'Determining the optimal balance between district and list seats is challenging'
      ],
      fairnessScore: 4,
      representationScore: 5,
      simplicity: 2,
      activeInCountries: 'Germany, New Zealand, Bolivia, Lesotho, Scotland, Wales'
    },
    {
      id: 'stv',
      title: 'Single Transferable Vote (STV)',
      icon: Shuffle,
      description: 'Voters rank candidates in order of preference in multi-member districts. Complex counting formula uses vote transfers to elect multiple candidates proportionally.',
      pros: [
        'High degree of voter choice among parties and candidates',
        'Minimizes wasted votes through preference transfers',
        'Achieves proportionality without formal party lists',
        'Encourages coalition-building and positive campaigning',
        'Maintains geographic connection with multiple representatives per area'
      ],
      cons: [
        "Complex counting procedure that's difficult to explain and implement",
        "Results can be hard for voters to understand or predict",
        "Time-consuming ballot counting process",
        "Encourages intra-party competition that can be divisive",
        "Requires relatively large multi-member districts to achieve proportionality"
      ],
      fairnessScore: 5,
      representationScore: 5,
      simplicity: 1,
      activeInCountries: 'Ireland, Malta, Australia (Senate), Northern Ireland, Scottish local elections'
    },
    {
      id: 'cumulative',
      title: 'Cumulative Voting',
      icon: Map,
      description: 'Voters receive multiple votes equal to the number of seats and can distribute them among candidates however they wish, including multiple votes for a single candidate.',
      pros: [
        'Gives voters flexibility in expressing preference strength',
        'Enables cohesive minority groups to elect representatives',
        'Simple to implement with existing voting infrastructure',
        'Can increase diversity of representation without redistricting',
        'Reduces wasted votes compared to plurality systems'
      ],
      cons: [
        'Requires strategic voting to maximize group representation',
        'May lead to bullet voting (concentrating all votes on one candidate)',
        'Does not guarantee proportional outcomes',
        'Can be confusing for voters used to one-vote systems',
        'Results can be unpredictable and depend on voter coordination'
      ],
      fairnessScore: 3,
      representationScore: 3,
      simplicity: 3,
      activeInCountries: 'Used for some local elections in the US, corporate board elections, historically used for Illinois House of Representatives (1870-1980)'
    }
  ];

  // Comparison data for redesigned table
  const comparisonData = [
    {
      system: 'Plurality/FPTP',
      type: 'Single-member',
      proportionality: 'Very Low',
      voterChoice: 'Low',
      complexity: 'Simple'
    },
    {
      system: 'At-Large Plurality',
      type: 'Multi-member',
      proportionality: 'Low-Medium',
      voterChoice: 'Medium',
      complexity: 'Simple'
    },
    {
      system: 'RCV/IRV',
      type: 'Single-member',
      proportionality: 'Medium',
      voterChoice: 'High',
      complexity: 'Medium'
    },
    {
      system: 'Open-List PR',
      type: 'Multi-member',
      proportionality: 'High',
      voterChoice: 'High',
      complexity: 'Medium'
    },
    {
      system: 'Closed-List PR',
      type: 'Multi-member',
      proportionality: 'High',
      voterChoice: 'Low',
      complexity: 'Simple'
    },
    {
      system: 'Mixed-Member PR',
      type: 'Hybrid',
      proportionality: 'High',
      voterChoice: 'High',
      complexity: 'Complex'
    },
    {
      system: 'STV',
      type: 'Multi-member',
      proportionality: 'Very High',
      voterChoice: 'Very High',
      complexity: 'Complex'
    },
    {
      system: 'Cumulative Voting',
      type: 'Multi-member',
      proportionality: 'Medium',
      voterChoice: 'Medium',
      complexity: 'Medium'
    }
  ];

  return (
    <div style={{ backgroundColor: colorPalette.light }}>
      {/* Hero Section */}
      <section style={{
        backgroundColor: colorPalette.dark,
        color: 'white',
        padding: isMobile ? '60px 0 40px' : '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <h1 style={{ 
            fontSize: isMobile ? '2.5rem' : '3rem',
            marginBottom: '16px',
            fontWeight: '800'
          }}>Voting Systems</h1>
          <p style={{
            fontSize: isMobile ? '1.1rem' : '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 24px',
            lineHeight: '1.6',
            opacity: '0.9'
          }}>
            Understanding the mechanics behind how we elect our representatives is crucial to building a better democracy.
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a href="#districting" style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              padding: isMobile ? '10px 16px' : '12px 20px',
              borderRadius: '5px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>Learn About Districting</a>
            <a href="#voting-methods" style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              padding: isMobile ? '10px 16px' : '12px 20px',
              borderRadius: '5px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>Explore Voting Methods</a>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section style={{
        padding: isMobile ? '40px 0' : '60px 0',
        backgroundColor: 'white'
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              color: colorPalette.dark,
              marginBottom: '24px'
            }}>Two Key Components of Electoral Systems</h2>
            
            <p style={{
              fontSize: isMobile ? '1rem' : '1.1rem',
              lineHeight: '1.6',
              color: '#444',
              marginBottom: '32px'
            }}>
              Electoral systems have two fundamental components that determine how votes translate to representation: 
              <strong style={{ color: colorPalette.primary }}> how we draw districts</strong> and 
              <strong style={{ color: colorPalette.primary }}> how we count votes</strong>. 
              Together, these components create dramatically different outcomes for voters and parties.
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '24px',
              marginTop: '40px',
              marginBottom: '40px',
              textAlign: 'left'
            }}>
              <div style={{
                backgroundColor: colorPalette.light,
                borderRadius: '8px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    backgroundColor: colorPalette.primary,
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px'
                  }}>
                    <Map size={20} color="white" />
                  </div>
                  <h3 style={{ margin: 0, fontSize: isMobile ? '1.3rem' : '1.5rem', color: colorPalette.dark }}>Districting</h3>
                </div>
                <p style={{ lineHeight: '1.6', marginBottom: '12px' }}>
                  Districting determines how voters are grouped geographically to elect representatives. The size and configuration of districts directly impacts which communities are represented.
                </p>
                <a href="#districting" style={{
                  color: colorPalette.primary,
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Learn more about districting
                  <span style={{ marginLeft: '4px' }}>→</span>
                </a>
              </div>
              
              <div style={{
                backgroundColor: colorPalette.light,
                borderRadius: '8px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    backgroundColor: colorPalette.secondary,
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px'
                  }}>
                    <Vote size={20} color="white" />
                  </div>
                  <h3 style={{ margin: 0, fontSize: isMobile ? '1.3rem' : '1.5rem', color: colorPalette.dark }}>Voting Methods</h3>
                </div>
                <p style={{ lineHeight: '1.6', marginBottom: '12px' }}>
                  Voting methods determine how voters express their preferences on a ballot and how those preferences are translated into winners. Different counting systems can produce vastly different outcomes.
                </p>
                <a href="#voting-methods" style={{
                  color: colorPalette.secondary,
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Learn more about voting methods
                  <span style={{ marginLeft: '4px' }}>→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Districting Section */}
      <section id="districting" style={{
        padding: isMobile ? '60px 0' : '80px 0',
        backgroundColor: colorPalette.light
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              color: colorPalette.dark,
              marginBottom: '16px',
              textAlign: 'center'
            }}>Understanding Districting</h2>
            
            <p style={{
              fontSize: isMobile ? '1rem' : '1.1rem',
              lineHeight: '1.6',
              color: '#444',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              Districting is how we divide voters into geographical units for electing representatives. The choice between single-member and multi-member districts fundamentally shapes who gets represented.
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '40px',
              marginBottom: '60px'
            }}>
              {/* Single Member Districts */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}>
                <div style={{
                  backgroundColor: colorPalette.dark,
                  padding: '24px',
                  color: 'white'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: isMobile ? '1.3rem' : '1.5rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Map size={24} style={{ marginRight: '12px' }} />
                    Single-Member Districts
                  </h3>
                </div>
                
                <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Description box */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: colorPalette.light,
                    borderRadius: '8px',
                    marginBottom: '24px'
                  }}>
                    <p style={{ margin: 0, fontStyle: 'italic', fontSize: '0.95rem' }}>
                      One representative elected from each geographical district
                    </p>
                  </div>
                  
                  {/* Visualization - simplified for mobile */}
                  <div className="smd-visualization" style={{
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}>
                    <svg width="100%" height={isMobile ? 120 : 160} viewBox={`0 0 400 ${isMobile ? 120 : 160}`}>
                      {/* District 1 */}
                      <rect x="30" y="30" width="100" height="100" rx="4" fill="#f5f5f5" stroke="#ddd" strokeWidth="2" />
                      <circle cx="80" cy="80" r="25" fill={colorPalette.primary} />
                      <text x="80" y="85" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">Rep 1</text>
                      <text x="80" y={isMobile ? 20 : 15} textAnchor="middle" fill="#666" fontSize={isMobile ? 10 : 12}>District 1</text>
                      
                      {/* District 2 */}
                      <rect x="150" y="30" width="100" height="100" rx="4" fill="#f5f5f5" stroke="#ddd" strokeWidth="2" />
                      <circle cx="200" cy="80" r="25" fill={colorPalette.primary} />
                      <text x="200" y="85" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">Rep 2</text>
                      <text x="200" y={isMobile ? 20 : 15} textAnchor="middle" fill="#666" fontSize={isMobile ? 10 : 12}>District 2</text>
                      
                      {/* District 3 */}
                      <rect x="270" y="30" width="100" height="100" rx="4" fill="#f5f5f5" stroke="#ddd" strokeWidth="2" />
                      <circle cx="320" cy="80" r="25" fill={colorPalette.primary} />
                      <text x="320" y="85" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">Rep 3</text>
                      <text x="320" y={isMobile ? 20 : 15} textAnchor="middle" fill="#666" fontSize={isMobile ? 10 : 12}>District 3</text>
                    </svg>
                  </div>
                  
                  {/* Content that needs to be aligned */}
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div>
                      {/* Key Features */}
                      <h4 style={{ 
                        fontSize: '1.1rem',
                        marginBottom: '12px',
                        marginTop: '20px',
                        color: colorPalette.dark
                      }}>Key Features:</h4>
                      
                      <ul style={{
                        listStyleType: 'none',
                        padding: 0,
                        margin: 0,
                        marginBottom: '24px'
                      }}>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.primary
                          }}></span>
                          <strong>Clear geographic representation</strong> - Each area has one dedicated representative
                        </li>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.primary
                          }}></span>
                          <strong>Winner-takes-all</strong> - Only voters who supported the winning candidate get representation
                        </li>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.primary
                          }}></span>
                          <strong>Highly susceptible to gerrymandering</strong> - District boundaries can be manipulated for partisan advantage
                        </li>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.primary
                          }}></span>
                          <strong>Often creates "wasted votes"</strong> - Votes for losing candidates don't contribute to representation
                        </li>
                      </ul>
                    </div>
                    
                    <div style={{ marginTop: 'auto', paddingTop: '24px' }}> {/* This pushes "Currently Used" to align with right card */}
                      {/* Currently Used */}
                      <h4 style={{ 
                        fontSize: '1.1rem',
                        marginBottom: '12px',
                        color: colorPalette.dark
                      }}>Currently Used:</h4>
                      
                      <p style={{ fontSize: '0.95rem', marginBottom: '24px' }}>
                        United States (Congress), United Kingdom, Canada, and most countries with plurality/majority systems
                      </p>
                    </div>
                  </div>
                  
                  {/* The Impact section - Always at bottom */}
                  <div style={{
                    backgroundColor: '#f9f5f3',
                    borderLeft: `4px solid ${colorPalette.primary}`,
                    padding: '16px',
                    borderRadius: '0 4px 4px 0',
                    marginTop: '24px'
                  }}>
                    <p style={{ fontSize: '0.9rem', margin: 0, fontStyle: 'italic' }}>
                      <strong>The Impact:</strong> Single-member districts often produce disproportionate outcomes where a party winning 51% of votes in each district could win 100% of the seats, leaving many voters without representation that shares their views.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Multi Member Districts */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}>
                <div style={{
                  backgroundColor: colorPalette.secondary,
                  padding: '24px',
                  color: 'white'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: isMobile ? '1.3rem' : '1.5rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Grid size={24} style={{ marginRight: '12px' }} />
                    Multi-Member Districts
                  </h3>
                </div>
                
                <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Description box */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: colorPalette.light,
                    borderRadius: '8px',
                    marginBottom: '24px'
                  }}>
                    <p style={{ margin: 0, fontStyle: 'italic', fontSize: '0.95rem' }}>
                      Multiple representatives elected from each geographical district
                    </p>
                  </div>
                  
                  {/* Visualization - simplified for mobile */}
                  <div className="mmd-visualization" style={{
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}>
                    <svg width="100%" height={isMobile ? 140 : 180} viewBox={`0 0 400 ${isMobile ? 140 : 180}`}>
                      {/* One district with multiple representatives */}
                      <rect x="50" y="30" width="300" height="100" rx="4" fill="#f5f5f5" stroke="#ddd" strokeWidth="2" />
                      
                      {/* Centered title */}
                      <text x="200" y={isMobile ? 20 : 15} textAnchor="middle" fill="#666" fontSize={isMobile ? 10 : 12}>One Larger District with Multiple Representatives</text>
                      
                      {/* Representatives */}
                      <circle cx="100" cy="80" r={isMobile ? 20 : 25} fill="#922c40" /> {/* Party A - wine red */}
                      <text x="100" y="85" textAnchor="middle" fill="white" fontWeight="bold" fontSize={isMobile ? 12 : 14}>Rep 1</text>
                      
                      <circle cx="166.67" cy="80" r={isMobile ? 20 : 25} fill="#DC9750" /> {/* Party B - bronze/gold */}
                      <text x="166.67" y="85" textAnchor="middle" fill="white" fontWeight="bold" fontSize={isMobile ? 12 : 14}>Rep 2</text>
                      
                      <circle cx="233.33" cy="80" r={isMobile ? 20 : 25} fill="#109618" /> {/* Party C - green */}
                      <text x="233.33" y="85" textAnchor="middle" fill="white" fontWeight="bold" fontSize={isMobile ? 12 : 14}>Rep 3</text>
                      
                      <circle cx="300" cy="80" r={isMobile ? 20 : 25} fill="#ff9900" /> {/* Party D - orange */}
                      <text x="300" y="85" textAnchor="middle" fill="white" fontWeight="bold" fontSize={isMobile ? 12 : 14}>Rep 4</text>
                      
                      {/* Party labels */}
                      <text x="100" y={isMobile ? 140 : 155} textAnchor="middle" fill="#922c40" fontSize={isMobile ? 8 : 10}>Party A</text>
                      <text x="166.67" y={isMobile ? 140 : 155} textAnchor="middle" fill="#DC9750" fontSize={isMobile ? 8 : 10}>Party B</text>
                      <text x="233.33" y={isMobile ? 140 : 155} textAnchor="middle" fill="#109618" fontSize={isMobile ? 8 : 10}>Party C</text>
                      <text x="300" y={isMobile ? 140 : 155} textAnchor="middle" fill="#ff9900" fontSize={isMobile ? 8 : 10}>Party D</text>
                    </svg>
                  </div>
                  
                  {/* Content that needs to be aligned */}
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div>
                      {/* Key Features */}
                      <h4 style={{ 
                        fontSize: '1.1rem',
                        marginBottom: '12px',
                        color: colorPalette.dark
                      }}>Key Features:</h4>
                      
                      <ul style={{
                        listStyleType: 'none',
                        padding: 0,
                        margin: 0,
                        marginBottom: '24px'
                      }}>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.secondary
                          }}></span>
                          <strong>Enables proportional representation</strong> - Different groups can win seats based on their share of the vote
                        </li>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.secondary
                          }}></span>
                          <strong>Reduces wasted votes</strong> - More voters see their preferences reflected in who gets elected
                        </li>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.secondary
                          }}></span>
                          <strong>Resistant to gerrymandering</strong> - District boundaries matter less when seats are allocated proportionally
                        </li>
                        <li style={{
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '9px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: colorPalette.secondary
                          }}></span>
                          <strong>Multiple representatives per area</strong> - Voters have several representatives they can approach
                        </li>
                      </ul>
                    </div>
                    
                    <div style={{ marginTop: 'auto', paddingTop: '24px' }}> {/* This ensures vertical alignment with left card */}
                      {/* Currently Used */}
                      <h4 style={{ 
                        fontSize: '1.1rem',
                        marginBottom: '12px',
                        color: colorPalette.dark
                      }}>Currently Used:</h4>
                      
                      <p style={{ fontSize: '0.95rem', marginBottom: '24px' }}>
                        Most European democracies, Ireland, Australia (Senate), New Zealand, Sweden, Finland, Denmark, Norway
                      </p>
                    </div>
                  </div>
                  
                  {/* The Impact section - Always at bottom */}
                  <div style={{
                    backgroundColor: '#f9f5f3',
                    borderLeft: `4px solid ${colorPalette.secondary}`,
                    padding: '16px',
                    borderRadius: '0 4px 4px 0',
                    marginTop: '24px'
                  }}>
                    <p style={{ fontSize: '0.9rem', margin: 0, fontStyle: 'italic' }}>
                      <strong>The Impact:</strong> Multi-member districts allow for more diverse representation, with seats distributed to reflect the actual voter preferences within the district. This enables minority views to gain representation and reduces "wasted votes."
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{
              marginTop: '40px',
              padding: '24px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              maxWidth: '900px',
              margin: '0 auto'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <div style={{
                  backgroundColor: colorPalette.primary,
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <Info size={20} color="white" />
                </div>
                <h3 style={{ margin: 0, fontSize: isMobile ? '1.3rem' : '1.5rem', color: colorPalette.dark }}>Why Districting Matters</h3>
              </div>
              
              <p style={{ fontSize: isMobile ? '0.95rem' : '1rem', lineHeight: '1.6', marginBottom: '16px' }}>
                The choice between single-member and multi-member districts is not just a technical detail—it fundamentally shapes our democracy:
              </p>
              
              <ul style={{
                listStyleType: 'none',
                padding: 0,
                margin: '0 0 16px 0'
              }}>
                <li style={{
                  marginBottom: '12px',
                  paddingLeft: '20px',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    top: '9px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: colorPalette.primary
                  }}></span>
                  <strong>Representation Quality:</strong> Multi-member districts typically result in legislatures that better reflect the diversity of voter opinion.
                </li>
                <li style={{
                  marginBottom: '12px',
                  paddingLeft: '20px',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    top: '9px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: colorPalette.primary
                  }}></span>
                  <strong>Gerrymandering:</strong> Single-member districts are highly vulnerable to partisan manipulation through district line-drawing.
                </li>
                <li style={{
                  marginBottom: '12px',
                  paddingLeft: '20px',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    top: '9px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: colorPalette.primary
                  }}></span>
                  <strong>Voting Power:</strong> In single-member districts, large numbers of voters may have no representative who shares their views.
                </li>
                <li style={{
                  marginBottom: '12px',
                  paddingLeft: '20px',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    top: '9px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: colorPalette.primary
                  }}></span>
                  <strong>Political Diversity:</strong> Multi-member districts enable the success of third parties and independent candidates who would otherwise be shut out.
                </li>
              </ul>
              
              <p style={{ fontSize: isMobile ? '0.95rem' : '1rem', fontStyle: 'italic' }}>
                Many electoral reform proposals focus on changing from single-member to multi-member districts as a foundation for more proportional representation and a more responsive democracy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voting Methods Section */}
      <section id="voting-methods" style={{
        padding: isMobile ? '60px 0' : '80px 0',
        backgroundColor: 'white'
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              color: colorPalette.dark,
              marginBottom: '16px',
              textAlign: 'center'
            }}>Voting Methods</h2>
            
            <p style={{
              fontSize: isMobile ? '1rem' : '1.1rem',
              lineHeight: '1.6',
              color: '#444',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              How votes are cast and counted significantly impacts which voices are heard and who gets elected. Voting methods work together with districting to determine representation.
            </p>
            
            <div style={{
              backgroundColor: colorPalette.dark,
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              marginBottom: '40px'
            }}>
              <h3 style={{ fontSize: isMobile ? '1.3rem' : '1.5rem', marginBottom: '16px' }}>Single-Member District Voting Methods</h3>
              <p style={{ fontSize: isMobile ? '0.95rem' : '1rem', opacity: '0.9' }}>
                These systems determine how representatives are elected from single-member districts where only one candidate can win.
              </p>
            </div>
            
            {smdSystems.map(system => (
              <SystemCard
                key={system.id}
                title={system.title}
                icon={system.icon}
                description={system.description}
                pros={system.pros}
                cons={system.cons}
                fairnessScore={system.fairnessScore}
                representationScore={system.representationScore}
                simplicity={system.simplicity}
                activeInCountries={system.activeInCountries}
                onClick={() => setActiveSystem(system.id)}
                isActive={activeSystem === system.id}
              />
            ))}
            <div style={{
              backgroundColor: colorPalette.dark,
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              marginTop: '60px',
              marginBottom: '40px'
            }}>
              <h3 style={{ fontSize: isMobile ? '1.3rem' : '1.5rem', marginBottom: '16px' }}>Multi-Member District Voting Methods</h3>
              <p style={{ fontSize: isMobile ? '0.95rem' : '1rem', opacity: '0.9' }}>
                These systems determine how multiple representatives are elected from larger districts, often resulting in more proportional representation.
              </p>
            </div>
            
            {mmdSystems.map(system => (
              <SystemCard
                key={system.id}
                title={system.title}
                icon={system.icon}
                description={system.description}
                pros={system.pros}
                cons={system.cons}
                fairnessScore={system.fairnessScore}
                representationScore={system.representationScore}
                simplicity={system.simplicity}
                activeInCountries={system.activeInCountries}
                onClick={() => setActiveSystem(system.id)}
                isActive={activeSystem === system.id}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Comparison Section - Redesigned for mobile */}
      <section style={{
        padding: isMobile ? '60px 0' : '80px 0',
        backgroundColor: colorPalette.light
      }}>
        <div className="container" style={{ 
          width: '90%',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              color: colorPalette.dark,
              marginBottom: '16px',
              textAlign: 'center'
            }}>Comparing Electoral Systems</h2>
            
            <p style={{
              fontSize: isMobile ? '1rem' : '1.1rem',
              lineHeight: '1.6',
              color: '#444',
              marginBottom: '40px',
              textAlign: 'center'
            }}>
              Different electoral systems produce dramatically different outcomes. The comparison below summarizes key differences between common systems. Voters Deserve More advocates for Open-List PR and STV due to their high levels of proportionality, voter engagement, and voter representation.
            </p>
            
            {/* Desktop Table View */}
            {!isMobile && (
              <div style={{
                overflowX: 'auto',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                padding: '24px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: `2px solid ${colorPalette.primary}`,
                        color: colorPalette.dark,
                        backgroundColor: colorPalette.tertiary,
                        position: 'sticky',
                        top: 0
                      }}>System</th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: `2px solid ${colorPalette.primary}`,
                        color: colorPalette.dark,
                        backgroundColor: colorPalette.tertiary,
                        position: 'sticky',
                        top: 0
                      }}>Type</th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: `2px solid ${colorPalette.primary}`,
                        color: colorPalette.dark,
                        backgroundColor: colorPalette.tertiary,
                        position: 'sticky',
                        top: 0
                      }}>Proportionality</th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: `2px solid ${colorPalette.primary}`,
                        color: colorPalette.dark,
                        backgroundColor: colorPalette.tertiary,
                        position: 'sticky',
                        top: 0
                      }}>Voter Choice</th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: `2px solid ${colorPalette.primary}`,
                        color: colorPalette.dark,
                        backgroundColor: colorPalette.tertiary,
                        position: 'sticky',
                        top: 0
                      }}>Complexity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((item, index) => (
                      <tr key={index}>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontWeight: '600' }}>{item.system}</td>
                      <td style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>{item.type}</td>
                      <td style={{ 
                        padding: '12px 16px', 
                        borderBottom: '1px solid #eee', 
                        color: item.proportionality.includes('High') ? '#2e7d32' : 
                              item.proportionality.includes('Medium') ? '#ef6c00' : '#d32f2f' 
                      }}>{item.proportionality}</td>
                      <td style={{ 
                        padding: '12px 16px', 
                        borderBottom: '1px solid #eee', 
                        color: item.voterChoice.includes('High') ? '#2e7d32' : 
                              item.voterChoice.includes('Medium') ? '#ef6c00' : '#d32f2f' 
                      }}>{item.voterChoice}</td>
                      <td style={{ 
                        padding: '12px 16px', 
                        borderBottom: '1px solid #eee', 
                        color: item.complexity.includes('Simple') ? '#2e7d32' : 
                              item.complexity.includes('Medium') ? '#ef6c00' : '#d32f2f' 
                      }}>{item.complexity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Mobile Card View - replaces table for better mobile experience */}
          {isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {comparisonData.map((item, index) => (
                <ComparisonCard
                  key={index}
                  system={item.system}
                  type={item.type}
                  proportionality={item.proportionality}
                  voterChoice={item.voterChoice}
                  complexity={item.complexity}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
    
    {/* Next Steps Section */}
    <section style={{
      padding: isMobile ? '60px 0' : '80px 0',
      backgroundColor: colorPalette.primary,
      color: 'white',
      textAlign: 'center'
    }}>
      <div className="container" style={{ 
        width: '90%',
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 1rem'
      }}>
        <h2 style={{
          fontSize: isMobile ? '2rem' : '2.5rem',
          marginBottom: '24px',
          fontWeight: '700'
        }}>Ready to Transform American Democracy?</h2>
        
        <p style={{
          fontSize: isMobile ? '1.1rem' : '1.25rem',
          marginBottom: '32px',
          lineHeight: '1.6',
          opacity: '0.9'
        }}>
          Understanding voting systems is the first step. Now it's time to take action and build a multiparty democracy that works for everyone.
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <Link to="/simulator" style={{
            backgroundColor: colorPalette.tertiary,
            color: colorPalette.dark,
            padding: isMobile ? '14px 24px' : '16px 28px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: isMobile ? '1rem' : '1.1rem',
            transition: 'transform 0.2s, background-color 0.2s'
          }}>
            Try Our Interactive Simulator
          </Link>
          
          <Link to="/get-involved" style={{
            backgroundColor: colorPalette.secondary,
            color: 'white',
            padding: isMobile ? '14px 24px' : '16px 28px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: isMobile ? '1rem' : '1.1rem',
            transition: 'transform 0.2s, background-color 0.2s'
          }}>
            Join the Movement
          </Link>
        </div>
      </div>
    </section>
  </div>
);
};

export default VotingSystems;